import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Table, TableBody } from 'src/components/ui/table';
import { useCallback, useEffect, useState } from 'react';
import { WorkflowsRow } from 'src/pages/ConfigurationPage/WorkflowsTab/WorkflowsTable/WorkflowsRow/WorkflowsRow';
import { clsx } from 'clsx';
import { WorkflowsDragableRow } from 'src/pages/ConfigurationPage/WorkflowsTab/WorkflowsTable/WorkflowsRow/WorkflowsDragableRow';
import type { Workflow } from 'src/pages/ConfigurationPage/WorkflowsTab/workflowFormValues';

export const WorkflowsTable = ({
  isCustom = false,
  workflows = [],
  readonly = true,
  compact = true,
  onChange = (_rows: Workflow[]) => {},
  rowClassName = '',
}: {
  isCustom?: boolean;
  workflows?: Workflow[];
  readonly?: boolean;
  compact?: boolean;
  onChange?: (rows: Workflow[]) => void;
  rowClassName?: string;
}) => {
  const [rows, setRows] = useState(workflows);

  const moveRow = useCallback((dragIndex: number, hoverIndex: number) => {
    setRows((prevRows) => {
      const newRows = [...prevRows];

      newRows.splice(dragIndex, 1);
      newRows.splice(hoverIndex, 0, prevRows[dragIndex]);

      onChange(newRows);

      return newRows;
    });
  }, []);

  useEffect(() => {
    setRows(
      workflows.map((workflow) => ({
        ...workflow,
        name: workflow.name || 'Escalation',
        description:
          workflow.description || workflow.type === 'default'
            ? 'When AI lacks the knowledge to assist.'
            : '',
      })),
    );
  }, [workflows]);

  const onWorkflowChange = (workflow: Workflow, index: number) => {
    const newRows = rows.map((row, idx) => (index === idx ? workflow : row));

    setRows(newRows);
    onChange(newRows);
  };

  const onWorkflowDelete = (index: number) => {
    const newRows = rows.filter((row, idx) => index !== idx);

    setRows(newRows);
    onChange(newRows);
  };

  const renderRow = useCallback(
    (row: Workflow, index: number) => {
      return isCustom ? (
        <WorkflowsDragableRow
          key={`row-${row.name || 'default'}-${row.type}`}
          index={index}
          row={row}
          moveRow={moveRow}
          readonly={readonly}
          isCustom={isCustom}
          compact={compact}
          onWorkflowChange={onWorkflowChange}
          onWorkflowDelete={onWorkflowDelete}
          rowClassName={rowClassName}
        />
      ) : (
        <WorkflowsRow
          key={`row-${row.name || 'default'}-${row.type}`}
          index={index}
          row={row}

          readonly={readonly}
          isCustom={isCustom}
          compact={compact}
          onWorkflowChange={onWorkflowChange}
          onWorkflowDelete={onWorkflowDelete}
          rowClassName={rowClassName}
        />
      );
    },
    [readonly, rows],
  );

  if (!rows.length) {
    return (
      <div className={clsx('px-5', !compact && 'pt-3.5')}>
        <p className={'text-ebony_opacity_30 text-sm font-medium'}>
          No {isCustom ? 'custom' : 'default'} workflows
        </p>
      </div>
    );
  }

  return isCustom ? (
    <DndProvider backend={HTML5Backend}>
      <Table className={clsx('w-full table-auto')}>
        <TableBody>{rows?.map((row, index) => renderRow(row, index))}</TableBody>
      </Table>
    </DndProvider>
  ) : (
    <Table className={clsx('w-full')}>
      <TableBody>{rows?.map((row, index) => renderRow(row, index))}</TableBody>
    </Table>
  );
};
